import { useState } from "react";
import type { ClientStatus } from "@mnp/shared";
import { Icon } from "../../components/Icon";
import { useClients } from "../../lib/useClients";
import { ClientDrawer } from "./ClientDrawer";
import { OrderDrawer, type OrderDrawerType } from "../orders/OrderDrawer";

const STATUS_FILTERS: { id: "all" | ClientStatus; label: string }[] = [
  { id: "all", label: "Все" },
  { id: "vip", label: "VIP" },
  { id: "active", label: "Активные" },
  { id: "inactive", label: "Неактивные" },
];

const STATUS_BADGE: Record<ClientStatus, { cls: string; label: string }> = {
  vip: { cls: "yellow", label: "⭐ VIP" },
  active: { cls: "green", label: "Активен" },
  inactive: { cls: "gray", label: "Неактивен" },
};

export function ClientsLanding() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | ClientStatus>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [openOrder, setOpenOrder] = useState<{ id: string; type: string } | null>(null);

  const { data, isLoading } = useClients({
    search,
    status: status === "all" ? undefined : status,
  });
  const rows = data?.rows ?? [];

  return (
    <div className="content">
      <div className="toolbar">
        <div className="search-inline">
          <Icon name="search" size={14} />
          <input
            placeholder="Поиск клиентов..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="cd-filter">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              data-active={status === f.id}
              onClick={() => setStatus(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Клиент</th>
              <th>Telegram</th>
              <th>Телефон</th>
              <th className="num">Заказов</th>
              <th className="num">Оборот</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: "center" }}>
                  Загрузка…
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    padding: 40,
                    textAlign: "center",
                    color: "var(--mnp-fg-3)",
                  }}
                >
                  Клиентов не найдено
                </td>
              </tr>
            )}
            {rows.map((c) => {
              const b = STATUS_BADGE[c.status];
              return (
                <tr
                  key={c.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => setOpenId(c.id)}
                >
                  <td>
                    <div className="f-row" style={{ gap: 10 }}>
                      <div
                        className={"cd-link-avatar status-" + c.status}
                        aria-hidden
                      >
                        {c.initials}
                      </div>
                      <div className="bold">{c.name}</div>
                    </div>
                  </td>
                  <td className="mono">{c.tgUsername ?? c.id}</td>
                  <td>{c.phone ?? "—"}</td>
                  <td className="num tnum bold">{c.stats.orders}</td>
                  <td className="num tnum bold">{c.stats.turnover}</td>
                  <td>
                    <span className={"badge-a " + b.cls}>{b.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openId && (
        <ClientDrawer
          clientId={openId}
          onClose={() => setOpenId(null)}
          onOpenClient={(id) => setOpenId(id)}
          onOpenOrder={(row) => {
            // ClientDrawer renders above OrderDrawer (z-index), so close it and
            // let the order card take over rather than hide behind the client.
            setOpenId(null);
            setOpenOrder(row);
          }}
        />
      )}

      {/* Order card opened from the client card. Stacks above ClientDrawer. */}
      {openOrder && (
        <OrderDrawer
          id={openOrder.id}
          type={openOrder.type as OrderDrawerType}
          onClose={() => setOpenOrder(null)}
        />
      )}
    </div>
  );
}
